import { useState } from 'react';
import { format, isEqual } from 'date-fns';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { SortingState } from '@tanstack/react-table';
import { ShortRestaurantLocation } from '@durma-soft/gros-sdk/dist/types/restaurants';
import {
  useAuthUserRestaurant,
  useGetAllRestaurantCompaniesQuery,
  useGetOrdersByFoodReportQuery,
} from '@durma-soft/gros-sdk';

import TableSizeSyncProvider from '@/providers/TableSizeSyncProvider';

import { useDatePickerRangeState } from '@/hooks/use-date-picker-range-state';

import { PrintReportButton } from '@/components/shared/print-report-button';
import { Skeleton } from '@/components/shared/shadcn-ui/skeleton';
import { LastUpdatedInfo } from '@/components/shared/last-updated-info';
import { OrdersByFood } from '@/components/restaurant/reports/orders-by-food';
import { DatePickerWithRange } from '@/components/shared/date-picker-with-range';
import { SelectCompanyLocations } from '@/components/shared/select-company-locations';

import print from '@/lib/print/print';

import { repeat } from '@/utils/helpers';

const OrdersByFoodReport = () => {
  const { t } = useTranslation();

  const { from, to, setDateRange } = useDatePickerRangeState();

  const [sorting, setSorting] = useState<SortingState>([]);
  const [companyLocations, setCompanyLocations] = useState<
    ShortRestaurantLocation[]
  >([]);

  const onUpdateCompanyLocations = (
    newCompanyLocations: ShortRestaurantLocation[],
  ) => {
    setCompanyLocations(newCompanyLocations);
  };

  const restaurant = useAuthUserRestaurant();

  const {
    data: companiesAndGroups,
    isSuccess: isSuccessCompanies,
    isLoading: isLoadingCompanies,
  } = useGetAllRestaurantCompaniesQuery(restaurant.id);

  const {
    data: ordersByFood = [],
    isLoading: isLoadingOrders,
    isSuccess: isSuccessOrders,
    dataUpdatedAt,
    isRefetching,
    refetch,
  } = useGetOrdersByFoodReportQuery({
    restaurant_id: restaurant.id,
    start_date: from,
    end_date: to,
    company_location_ids: companyLocations.length
      ? companyLocations.map(({ id }) => id)
      : [],
  });

  const handleRefetch = async () => {
    await refetch();
  };

  return (
    <>
      <Helmet>
        <title>{t('reports.orders-by-food.title')}</title>
      </Helmet>
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold md:text-2xl">
          {t('reports.orders-by-food.title')}
        </h1>
        {(isLoadingCompanies || isLoadingOrders) && (
          <Skeleton className="h-10 w-48" />
        )}
        {isSuccessCompanies && isSuccessOrders && (
          <LastUpdatedInfo
            dataUpdatedAt={dataUpdatedAt}
            isRefetching={isRefetching}
            handleRefetch={handleRefetch}
          />
        )}
      </div>
      <div className="flex flex-col gap-3 my-5 lg:flex-row">
        <div className="flex-1 flex flex-col gap-3 lg:flex-row ">
          <div className="self-start">
            <DatePickerWithRange
              date={{
                from: new Date(from),
                to: new Date(to),
              }}
              setDate={([from, to]) => {
                setDateRange({ from, to });
              }}
            />
          </div>
          <div>
            <SelectCompanyLocations
              companies={companiesAndGroups?.companies || []}
              groups={companiesAndGroups?.groups || []}
              isLoading={isLoadingCompanies}
              selectedCompanyLocations={companyLocations}
              onUpdateCompanyLocations={onUpdateCompanyLocations}
            />
          </div>
        </div>
        <div className="justify-start lg:justify-end flex">
          <PrintReportButton
            disabled={isLoadingCompanies || isLoadingOrders}
            onPrint={(fontScale) =>
              print({
                fontScale,
                content: (
                  <div>
                    <div className="flex-between">
                      <h1>{t('reports.orders-by-food.title')} </h1>
                      <div className="flex-row">
                        <p>{t('common.data-updated-at')}</p>
                        <span>&nbsp;</span>
                        <p>
                          <span> {format(dataUpdatedAt, 'dd.MM.yyyy')}</span>
                          <span> {t('common.in')} </span>
                          <span> {format(dataUpdatedAt, 'HH:mm:ss')}</span>
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col gap-10">
                      {ordersByFood.map(({ name, id, foods }) => (
                        <div
                          key={id}
                          className="flex flex-col gap-4 print-in-separate-page"
                        >
                          <p>
                            {format(from, 'dd.MM.yyyy')}
                            {!isEqual(from, to) &&
                              ` - ${format(to, 'dd.MM.yyyy')}`}
                          </p>
                          <h3 className="text-xl font-medium">{name}</h3>
                          <OrdersByFood
                            foods={foods}
                            forPrint
                            sorting={sorting}
                            setSorting={setSorting}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ),
              })
            }
          />
        </div>
      </div>
      <div className="w-full flex flex-col">
        {(isLoadingCompanies || isLoadingOrders) && (
          <div className="flex flex-col gap-10">
            {repeat(5).map((index) => (
              <div key={index}>
                <Skeleton className="w-full rounded-md h-60" />
              </div>
            ))}
          </div>
        )}

        {!isLoadingOrders &&
          ordersByFood.length === 0 &&
          companyLocations.length !== 0 && (
            <p className="text-center">{t('common.no-results')}</p>
          )}

        {!isLoadingCompanies && companyLocations.length === 0 && (
          <p className="self-center">{t('reports.orders.choose-location')}</p>
        )}

        {isSuccessOrders && !isLoadingOrders && (
          <div className="flex-1">
            <TableSizeSyncProvider>
              <div className="flex flex-col gap-10">
                {ordersByFood.map(({ name, id, foods }, index) => (
                  <div
                    key={id}
                    className="flex flex-col gap-4 print-in-separate-page"
                  >
                    <h3 className="text-xl font-medium">{name}</h3>
                    <OrdersByFood
                      foods={foods}
                      sorting={sorting}
                      setSorting={setSorting}
                      isTableSyncReference={index === 0}
                    />
                  </div>
                ))}
              </div>
            </TableSizeSyncProvider>
          </div>
        )}
      </div>
    </>
  );
};

export default OrdersByFoodReport;
